import { BRAND_NAME } from '~/common/constants/brand';
import { CrmType } from '~/enums/CrmType';
import { DocumentEditingIntegrationType } from '~/enums/DocumentEditingIntegrationType';
import { EsignatureType } from '~/enums/EsignatureType';
import { ProcurementIntegrationType } from '~/enums/ProcurementIntegrationType';
import { StorageProviderType } from '~/enums/StorageProviderType';
import { intlService } from '~/i18n/IntlService';

interface Integration<ProviderType> {
  inactiveMessage?: string;
  name: string;
  provider: ProviderType;
  title?: string;
}

export const getEsignatureIntegrations = (): Record<
  EsignatureType,
  Integration<EsignatureType>
> => {
  const intl = intlService.getIntl();
  return {
    [EsignatureType.Adobesign]: {
      name: intl.formatMessage({
        id: 'Integrations.adobeSignName',
        defaultMessage: 'Adobe Sign',
        description: 'Name for Adobe Sign e-signature integration',
      }),
      provider: EsignatureType.Adobesign,
    },
    [EsignatureType.Docusign]: {
      name: intl.formatMessage({
        id: 'Integrations.docusignName',
        defaultMessage: 'Docusign',
        description: 'Name for Docusign e-signature integration',
      }),
      provider: EsignatureType.Docusign,
    },
    [EsignatureType.DocusignDemo]: {
      name: intl.formatMessage({
        id: 'Integrations.docusignDemoName',
        defaultMessage: 'DocuSign Demo',
        description: 'Name for DocuSign Demo e-signature integration',
      }),
      provider: EsignatureType.DocusignDemo,
    },
  };
};

export const getCrmIntegrations = (): Record<CrmType, Integration<CrmType>> => {
  const intl = intlService.getIntl();
  return {
    [CrmType.Salesforce]: {
      name: intl.formatMessage({
        id: 'Integrations.salesforceName',
        defaultMessage: 'Salesforce',
        description: 'Name for Salesforce CRM integration',
      }),
      provider: CrmType.Salesforce,
      title: intl.formatMessage(
        {
          id: 'Integrations.salesforceTitle',
          defaultMessage: '{brandName} Contract Management for Salesforce',
          description: 'Title for Salesforce CRM integration',
        },
        { brandName: BRAND_NAME },
      ),
    },
  };
};

export const getProcurementIntegrations = (): Record<
  ProcurementIntegrationType,
  Integration<ProcurementIntegrationType>
> => {
  const intl = intlService.getIntl();
  return {
    [ProcurementIntegrationType.Ariba]: {
      name: intl.formatMessage({
        id: 'Integrations.aribaName',
        defaultMessage: 'Ariba',
        description: 'Name for Ariba procurement integration',
      }),
      provider: ProcurementIntegrationType.Ariba,
      title: intl.formatMessage(
        {
          id: 'Integrations.aribaTitle',
          defaultMessage: '{brandName} Procurement Management for Ariba',
          description: 'Title for Ariba procurement integration',
        },
        { brandName: BRAND_NAME },
      ),
    },
  };
};

export const getStorageIntegrations = (): Record<
  StorageProviderType,
  Integration<StorageProviderType>
> => {
  const intl = intlService.getIntl();
  return {
    [StorageProviderType.Dropbox]: {
      name: intl.formatMessage({
        id: 'Integrations.dropboxName',
        defaultMessage: 'Dropbox',
        description: 'Name for Dropbox storage integration',
      }),
      provider: StorageProviderType.Dropbox,
    },
    [StorageProviderType.GoogleDrive]: {
      name: intl.formatMessage({
        id: 'Integrations.googleDriveName',
        defaultMessage: 'Google Drive',
        description: 'Name for Google Drive storage integration',
      }),
      provider: StorageProviderType.GoogleDrive,
    },
    [StorageProviderType.AmazonS3]: {
      name: intl.formatMessage({
        id: 'Integrations.amazonS3Name',
        defaultMessage: 'Amazon S3',
        description: 'Name for Amazon S3 storage integration',
      }),
      provider: StorageProviderType.AmazonS3,
    },
    [StorageProviderType.Box]: {
      name: intl.formatMessage({
        id: 'Integrations.boxName',
        defaultMessage: 'Box',
        description: 'Name for Box storage integration',
      }),
      provider: StorageProviderType.Box,
    },
    [StorageProviderType.OneDrive]: {
      name: intl.formatMessage({
        id: 'Integrations.oneDriveName',
        defaultMessage: 'OneDrive',
        description: 'Name for OneDrive storage integration',
      }),
      provider: StorageProviderType.OneDrive,
    },
    [StorageProviderType.SharePoint]: {
      name: intl.formatMessage({
        id: 'Integrations.sharePointName',
        defaultMessage: 'SharePoint',
        description: 'Name for SharePoint storage integration',
      }),
      provider: StorageProviderType.SharePoint,
    },
  };
};

export const getDocumentEditingIntegrations = (): Record<
  DocumentEditingIntegrationType,
  Integration<DocumentEditingIntegrationType>
> => {
  const intl = intlService.getIntl();
  return {
    [DocumentEditingIntegrationType.Word]: {
      name: intl.formatMessage({
        id: 'Integrations.microsoftWordName',
        defaultMessage: 'Microsoft Word',
        description: 'Name for Microsoft Word document editing integration',
      }),
      provider: DocumentEditingIntegrationType.Word,
    },
  };
};
